import {
  Button as ChakraButton,
  ButtonProps,
  HStack,
  Icon,
  IconButton as ChakraIconButton,
  MenuButton,
  StackProps,
} from '@chakra-ui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Text } from '@main/complykit/components/text';
import { MouseEvent, ReactNode } from 'react';

export type MenuTriggerButtonProps = Omit<ButtonProps, 'children'> & {
  children: ReactNode;
  clearLabel?: string;
  containerProps?: StackProps;
  isEmpty?: boolean;
  onClearClick?: () => void;
};

export function MenuTriggerButton({
  children,
  clearLabel,
  containerProps,
  isEmpty,
  onClearClick,
  rightIcon,
  maxW,
  minW,
  w,
  width,
  ...buttonProps
}: MenuTriggerButtonProps) {
  return (
    <HStack
      spacing={0}
      display="inline-flex"
      maxW={maxW}
      minW={minW}
      w={w ?? width}
      {...containerProps}
    >
      <MenuButton
        // Parked with the Select round: the menu-select engine's trigger converts
        // with the engine, as button.md rules for every MenuButton position.
        as={ChakraButton}
        variant="outline"
        colorScheme="gray"
        rightIcon={onClearClick ? undefined : rightIcon}
        borderRightRadius={onClearClick ? 0 : buttonProps.borderRightRadius}
        flex={1}
        minW={0}
        overflow="hidden"
        // Chakra Button defaults to whiteSpace nowrap, which is inherited and
        // breaks child line-clamp / ellipsis. Text owns wrapping itself.
        whiteSpace="normal"
        fontSize="sm"
        fontWeight="normal"
        justifyContent="space-between"
        {...buttonProps}
      >
        <Text
          as="span"
          align="start"
          size="sm"
          color={isEmpty ? 'muted' : 'default'}
          isTruncated
          isGrow
        >
          {children}
        </Text>
      </MenuButton>
      {onClearClick && (
        <ChakraIconButton
          aria-label={clearLabel ?? 'Clear'}
          icon={<Icon as={XMarkIcon} boxSize={4} />}
          colorScheme="gray"
          variant="outline"
          // The attached clear segment follows the trigger's size, so a
          // compact trigger cannot carry a taller X.
          size={buttonProps.size}
          borderLeft={0}
          borderLeftRadius={0}
          isDisabled={buttonProps.isDisabled}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            onClearClick();
          }}
        />
      )}
    </HStack>
  );
}
